import { Component, effect } from '@angular/core';
import { CommonModule } from '@angular/common';

import { RouterModule } from '@angular/router';
import { NotificationsService } from '../../services/notifications.service';
import { AppNotification } from '@models';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.page.html',
  styleUrls: ['./notifications.page.scss'],
  standalone: true,
  imports: [RouterModule, CommonModule],
})
export class NotificationsPage {
  activeTab: 'all' | 'unread' | 'mentions' | 'system' | 'invites' | 'archived' = 'all';

  notifications: AppNotification[] = [];
  invites: any[] = [];

  constructor(private notificationsSvc: NotificationsService) {
    effect(() => {
      this.notifications = this.notificationsSvc.notifications();
      this.invites = this.notificationsSvc.invites();
    });
  }

  private pendingKeys = new Set<string>();

  private key(kind: 'notification' | 'invite', id: string | number, action: 'accept' | 'decline'): string {
    return `${kind}:${String(id)}:${action}`;
  }

  isInviteActionPendingForNotification(n: AppNotification): boolean {
    return this.pendingKeys.has(this.key('notification', n.id, 'accept')) || this.pendingKeys.has(this.key('notification', n.id, 'decline'));
  }

  isInviteActionPendingForInviteRow(inviteId: string | number): boolean {
    return this.pendingKeys.has(this.key('invite', inviteId, 'accept')) || this.pendingKeys.has(this.key('invite', inviteId, 'decline'));
  }

  isPending(kind: 'notification' | 'invite', id: string | number, action: 'accept' | 'decline'): boolean {
    return this.pendingKeys.has(this.key(kind, id, action));
  }

  private setPending(kind: 'notification' | 'invite', id: string | number, action: 'accept' | 'decline', pending: boolean) {
    const k = this.key(kind, id, action);
    // Reassign to ensure change detection sees the update even under OnPush.
    const next = new Set(this.pendingKeys);
    if (pending) next.add(k);
    else next.delete(k);
    this.pendingKeys = next;
  }

  get visibleNotifications() {
    const active = this.notifications.filter((n) => !n.archived);
    if (this.activeTab === 'unread') {
      return active.filter((n) => !n.read);
    }
    if (this.activeTab === 'mentions') {
      return active.filter((n) => n.category === 'mention');
    }
    if (this.activeTab === 'system') {
      return active.filter((n) => n.category === 'system');
    }
    if (this.activeTab === 'archived') {
      return this.notifications.filter((n) => n.archived);
    }
    return active;
  }

  get unreadCount(): number {
    return this.notificationsSvc.unreadCount();
  }

  setTab(tab: 'all' | 'unread' | 'mentions' | 'system' | 'invites' | 'archived') {
    this.activeTab = tab;
  }

  markAsRead(notificationId: string) {
    void this.notificationsSvc.markAsRead(notificationId);
  }

  markAllAsRead() {
    void this.notificationsSvc.markAllAsRead();
  }

  archive(n: AppNotification) {
    void this.notificationsSvc.archive(n.id);
  }

  restore(n: AppNotification) {
    void this.notificationsSvc.restore(n.id);
  }

  isInviteNotification(n: AppNotification): boolean {
    return n.category === 'invite' && Boolean((n as any)?.data?.inviteId) && Boolean((n as any)?.data?.inviteType);
  }

  async acceptInviteNotification(n: AppNotification) {
    if (this.isInviteActionPendingForNotification(n)) return;
    this.setPending('notification', n.id, 'accept', true);
    try {
      await this.notificationsSvc.acceptInviteFromNotification(n);
      // Mark the notification read after handling.
      await this.notificationsSvc.markAsRead(n.id);
    } finally {
      this.setPending('notification', n.id, 'accept', false);
    }
  }

  async declineInviteNotification(n: AppNotification) {
    if (this.isInviteActionPendingForNotification(n)) return;
    this.setPending('notification', n.id, 'decline', true);
    try {
      await this.notificationsSvc.declineInviteFromNotification(n);
      await this.notificationsSvc.markAsRead(n.id);
    } finally {
      this.setPending('notification', n.id, 'decline', false);
    }
  }

  async acceptInvite(inviteId: number) {
    if (this.isInviteActionPendingForInviteRow(inviteId)) return;
    this.setPending('invite', inviteId, 'accept', true);
    try {
      await this.notificationsSvc.acceptInvite(String(inviteId));
    } finally {
      this.setPending('invite', inviteId, 'accept', false);
    }
  }

  async declineInvite(inviteId: number) {
    if (this.isInviteActionPendingForInviteRow(inviteId)) return;
    this.setPending('invite', inviteId, 'decline', true);
    try {
      await this.notificationsSvc.declineInvite(String(inviteId));
    } finally {
      this.setPending('invite', inviteId, 'decline', false);
    }
  }
}